<template>
  <div id="po_receive" :class="isEditing ? 'container-edit-mode' : 'container'">
    <div class="actions exclude-print">
      <button
        v-if="isEditing"
        @click="submitOrderItems"
        class="btn-submit"
        :disabled="isSubmitting"
      >
        {{ isSubmitting ? "Saving..." : "Submit Changes" }}
      </button>
      <button
        v-if="is_delivered != 1 && is_delivered != '1'"
        @click="toggleEditMode"
        class="btn-edit"
      >
        {{ isEditing ? "Cancel Edit" : "Receive Delivery" }}</button
      >.

      <button
        v-if="is_delivered != 1 && is_delivered != '1' && !isEditing"
        @click="show_po_processed_pop"
        class="btn-edit-purple"
      >
        Complete and Transfer to store
      </button>

      <button
        v-if="!isEditing"
        @click="print_fn('po_receive', s_po_number + '_delivery')"
        class="btn-submit"
      >
        Print
      </button>
    </div>

    <h2 class="page-title">Purchase Order ID # {{ s_po_id }}</h2>

    <div class="order-info">
      <p><strong>Order Number:</strong> {{ s_po_number }}</p>
      <p><strong>Supplier:</strong> {{ s_supplier_name }}</p>
      <p><strong>Date:</strong> {{ s_order_date }}</p>
      <p><strong>Status:</strong> {{ s_status }}</p>
    </div>

    <h3 class="section-title">Delivery Items</h3>

    <table class="order-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Item Name</th>
          <th>SKU</th>
          <th>Expiry Date</th>
          <th>Quantity</th>
          <th>Unit Price</th>
          <th>Amount</th>
          <th>Description</th>
          <th>Delivered Qty</th>
          <th>Delivered Amount</th>
          <th>Spoilt Units</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in order.items" :key="item.id">
          <td>{{ index + 1 }}</td>
          <td>{{ item.item_name }}</td>

          <!-- SKU -->
          <td v-if="isEditing">
            <input type="text" v-model="item.sku" class="table-input" />
          </td>
          <td v-else>{{ item.sku }}</td>

          <!-- Expiry Date -->
          <td v-if="isEditing">
            <input type="date" v-model="item.expiry_date" class="table-input" />
          </td>
          <td v-else>{{ item.expiry_date }}</td>

          <!-- Quantity -->
          <td v-if="isEditing">
            <input
              type="number"
              v-model.number="item.quantity"
              @input="updateAmount(item)"
              class="table-input"
            />
          </td>
          <td v-else>{{ item.quantity }}</td>

          <!-- Unit Price -->
          <td v-if="isEditing">
            <input
              type="number"
              v-model.number="item.unit_price"
              @input="updateAmount(item)"
              class="table-input"
            />
          </td>
          <td v-else>{{ formatCurrency(item.unit_price) }}</td>

          <td>{{ formatCurrency(item.amount) }}</td>

          <!-- Description (Always read-only) -->
          <td>{{ item.description }}</td>

          <!-- Delivered Qty -->
          <td v-if="isEditing">
            <input
              type="number"
              v-model.number="item.delivered_quantity"
              @input="updateDeliveredAmount(item)"
              class="table-input"
            />
          </td>
          <td v-else>{{ item.delivered_quantity || 0 }}</td>

          <td>{{ formatCurrency(item.delivered_amount || 0) }}</td>

          <!-- Spoilt Units -->
          <td v-if="isEditing">
            <input
              type="number"
              v-model.number="item.spoilt_units"
              @input="updateSpoiltValue(item)"
              class="table-input"
            />
          </td>
          <td v-else>{{ item.spoilt_units || 0 }}</td>
        </tr>
      </tbody>
    </table>

    <div class="total-section actions">
      <p><strong>Total Amount:</strong> {{ formatCurrency(totalAmount) }}</p>
      <p>
        <strong>Total Delivered Value:</strong>
        {{ formatCurrency(totalDeliveredAmount) }}
      </p>
      <p>
        <strong>Total Spoilt Value:</strong>
        {{ formatCurrency(totalSpoiltValue) }}
      </p>
    </div>
  </div>
  <po_store_modal
    @close-process-modal="hide_po_processed_pop"
    :po_id="s_po_id"
    v-if="show_po_processed_modal"
  />
</template>

<script>
import { generalApiRequest } from "@/api/apiIndex";
import printPDF from "../../prints/div_to_pdf";
import po_store_modal from "./modals/po_store_modal.vue";

export default {
  name: "PurchaseOrderReceive",
  data() {
    return {
      order: {
        id: "",
        po_number: "",
        supplier_name: "",
        order_date: "",
        status: "",
        items: [],
      },
      show_po_processed_modal: false,
      is_delivered: "",
      s_po_id: "",
      s_po_number: "",
      s_supplier_name: "",
      s_order_date: "",
      s_status: "",
      isEditing: false,
      isSubmitting: false,
    };
  },
  components: {
    po_store_modal,
  },
  computed: {
    totalAmount() {
      return this.order.items.reduce(
        (sum, i) => sum + (parseFloat(i.amount) || 0),
        0
      );
    },
    totalDeliveredAmount() {
      return this.order.items.reduce(
        (sum, i) => sum + (parseFloat(i.delivered_amount) || 0),
        0
      );
    },
    totalSpoiltValue() {
      return this.order.items.reduce(
        (sum, i) =>
          sum +
          (parseFloat(i.spoilt_units) || 0) * (parseFloat(i.unit_price) || 0),
        0
      );
    },
  },
  methods: {
    show_po_processed_pop() {
      this.show_po_processed_modal = true;
    },
    hide_po_processed_pop() {
      this.show_po_processed_modal = false;
      this.isEditing = false;
      this.fetchOrderDetails();
    },
    print_fn(div_id, doc_name) {
      return printPDF(div_id, doc_name);
    },
    async fetchOrderDetails() {
      const orderId = this.$route.params.orderID;
      const res = await generalApiRequest(
        `inventory/purchase-orders/show/${orderId}`,
        "GET",
        {},
        1
      );

      this.order = {
        ...res.order,
        items: res.items.map((item) => ({
          ...item,
          amount: parseFloat(item.quantity) * parseFloat(item.unit_price),
          delivered_quantity: item.delivered_quantity || 0,
          delivered_amount: item.delivered_amount || 0,
          spoilt_units: item.spoilt_units || 0,
          sku: item.sku || "",
          expiry_date: item.expiry_date || "",
        })),
      };

      this.s_po_id = res.order.id;
      this.s_po_number = res.order.po_number;
      this.s_supplier_name = res.order.supplier_name;
      this.s_order_date = res.order.order_date;
      this.s_status = res.order.status;
      this.is_delivered = res.order.is_delivered;
    },
    toggleEditMode() {
      this.isEditing = !this.isEditing;
      if (!this.isEditing) this.fetchOrderDetails();
    },
    updateAmount(item) {
      item.amount = item.quantity * item.unit_price;
      this.updateDeliveredAmount(item);
      this.updateSpoiltValue(item);
    },
    updateDeliveredAmount(item) {
      item.delivered_amount = item.delivered_quantity * item.unit_price;
    },
    updateSpoiltValue() {
      // Nothing needed here, computed totalSpoiltValue handles it
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES",
      }).format(amount);
    },
    async submitOrderItems() {
      this.isSubmitting = true;
      try {
        const payload = {
          items: this.order.items.map(
            ({
              id,
              quantity,
              unit_price,
              amount,
              delivered_quantity,
              delivered_amount,
              spoilt_units,
              sku,
              expiry_date,
            }) => ({
              id,
              quantity,
              unit_price,
              amount,
              delivered_quantity,
              delivered_amount,
              spoilt_units,
              sku,
              expiry_date,
            })
          ),
        };

        await generalApiRequest(
          `inventory/purchase-orders/update-items/${this.order.id}`,
          "POST",
          payload,
          1
        );
        this.isEditing = false;
        await this.fetchOrderDetails();
        alert("Order items updated successfully.");
      } catch (error) {
        alert("Failed to update order items.");
        console.error(error);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
  mounted() {
    this.fetchOrderDetails();
  },
};
</script>

<style scoped>
.container-edit-mode {
  position: absolute;
  width: 100vw;
  z-index: 2000;
  top: 0;
  left: 0;
  margin: 20px auto;
  padding: 0 20px;
  border: 1px solid black;
  background-color: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}
.container {
  max-width: 960px;
  margin: 20px auto;
  padding: 0 20px;
  border: 1px solid black;
  background-color: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}
.page-title {
  font-size: 26px;
  font-weight: 600;
  margin-bottom: 20px;
}
.order-info p {
  margin: 4px 0;
  font-size: 15px;
}
.section-title {
  font-size: 20px;
  font-weight: 500;
  margin: 20px 0 10px;
}
.order-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 30px;
}
.order-table th,
.order-table td {
  border: 1px solid #ccc;
  padding: 10px 8px;
  text-align: left;
  font-size: 14px;
}
.order-table th {
  background-color: #f2f2f2;
}
.actions {
  text-align: right;
  margin-bottom: 1rem;
}
.table-input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  text-align: right;
  box-sizing: border-box;
}
.total-section {
  text-align: right;
  font-size: 16px;
  margin: 15px 0 20px;
  padding-right: 5px;
}
.btn-edit-purple {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #fff;
  background-color: #c9257a;
}
.btn-edit,
.btn-submit {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #fff;
}
.btn-edit {
  background-color: #0077cc;
}
.btn-edit:hover {
  background-color: #005fa3;
}
.btn-submit {
  background-color: #28a745;
  margin-left: 10px;
}
.btn-submit:disabled {
  background-color: #94d3a2;
  cursor: not-allowed;
}
</style>
